import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from './AuthContext'

const WebSocketContext = createContext()

// --- Constants & Configuration ---
const PING_INTERVAL_MS = 30000
const RECONNECT_DELAY_ANDROID = 500
const RECONNECT_DELAY_MOBILE = 1000
const RECONNECT_DELAY_DESKTOP = 3000
const RECONNECT_MAX_DELAY_MS = 10000
const WATCHDOG_LONG_DELAY_MS = 5000 // Delay after max attempts are exhausted before restarting cycle
const MAX_FAST_RECONNECT_ATTEMPTS = 3

// --- Helper Functions ---
const isAndroid = () => /Android/i.test(navigator.userAgent)
const isMobile = () => /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

export function useWebSocket() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}

// Get the appropriate WebSocket URL based on environment
const getWebSocketURL = () => {
  if (process.env.REACT_APP_WS_URL) return process.env.REACT_APP_WS_URL

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = window.location.hostname
  const isProduction = process.env.NODE_ENV === 'production'

  if (isProduction) {
    const port = window.location.port || (window.location.protocol === 'https:' ? '443' : '80')
    return `${protocol}//${host}:${port}/api/v1`
  } else {
    return `${protocol}//${host}:8080/api/v1`
  }
}

const WS_BASE_URL = getWebSocketURL()

export function WebSocketProvider({ children }) {
  const { session } = useAuth()
  const accessToken = session?.access_token
  
  const [socket, setSocket] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  
  // Refs
  /* eslint-disable no-unused-vars */
  const eventBusRef = useRef(new Map())
  const sessionRef = useRef(session)
  const connectRef = useRef(() => {})
  const reconnectTimeoutRef = useRef(null)
  /* eslint-enable no-unused-vars */
  
  const heartbeatIntervalRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const shouldReconnectRef = useRef(true) // Explicit flag to control reconnection

  // Update session ref
  useEffect(() => {
    sessionRef.current = session
  }, [session])

  // --- Reconnection Logic ---
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) return
    if (!shouldReconnectRef.current) return
    if (!sessionRef.current?.access_token) return // Don't reconnect if no token

    reconnectAttemptsRef.current++

    const isCurrentAndroid = isAndroid()
    const isCurrentMobile = isMobile()

    let delay
    
    // If we haven't exhausted fast attempts
    if (reconnectAttemptsRef.current <= MAX_FAST_RECONNECT_ATTEMPTS) {
        let base = isCurrentAndroid ? RECONNECT_DELAY_ANDROID : (isCurrentMobile ? RECONNECT_DELAY_MOBILE : RECONNECT_DELAY_DESKTOP)
        // Exponential backoff
        delay = Math.min(base * Math.pow(1.5, reconnectAttemptsRef.current - 1), RECONNECT_MAX_DELAY_MS)
    } else {
        // We exhausted fast attempts, wait longer then restart the cycle (Infinite Retry)
        console.log(`Max fast reconnect attempts reached. Waiting ${WATCHDOG_LONG_DELAY_MS}ms before restarting cycle.`)
        delay = WATCHDOG_LONG_DELAY_MS
        reconnectAttemptsRef.current = 0 // Reset for next cycle
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null
      connectRef.current?.()
    }, delay)
  }, [])

  // --- Connection Logic ---
  const connect = useCallback(() => {
    const token = sessionRef.current?.access_token
    if (!token) return
    if (socket?.readyState === WebSocket.OPEN) return

    setConnectionStatus('connecting')
    shouldReconnectRef.current = true

    try {
      const wsUrl = `${WS_BASE_URL}/ws?token=${encodeURIComponent(token)}`
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        const isCurrentAndroid = isAndroid()

        setConnectionStatus('connected')
        setSocket(ws)
        reconnectAttemptsRef.current = 0

        // Clear any pending reconnect
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
          reconnectTimeoutRef.current = null
        }

        // Android-specific: dispatch custom event
        if (isCurrentAndroid && reconnectAttemptsRef.current > 0) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('websocket-reconnected'))
          }, 500)
        }

        // Start Heartbeat
        if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current)
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, PING_INTERVAL_MS)
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          
          // Event Bus Dispatch
          const listeners = eventBusRef.current.get(message.type)
          if (listeners) {
            listeners.forEach(callback => {
              try {
                callback(message)
              } catch (err) {
                console.error(`Error in WebSocket listener for ${message.type}:`, err)
              }
            })
          }

        } catch (err) {
          console.error('Failed to parse WebSocket message:', err)
          // Legacy error handling for Game
          if (event.data.includes('"type":"game_state"')) {
            window.dispatchEvent(new CustomEvent('websocket-parse-error', {
              detail: { type: 'game_state', error: err.message }
            }))
          }
        }
      }

      ws.onclose = (event) => {
        setConnectionStatus('disconnected')
        setSocket(null)

        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
          heartbeatIntervalRef.current = null
        }

        const isAuthError = event.code === 1008 || event.code === 4001 || event.code === 4003

        if (event.code !== 1000 && !isAuthError && shouldReconnectRef.current) {
          scheduleReconnect()
        } else if (isAuthError) {
          console.log('WebSocket closed due to auth error. Stopping reconnection.')
          shouldReconnectRef.current = false
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        setConnectionStatus('error')
        // onerror usually followed by onclose, so we let onclose handle reconnect
      }

    } catch (err) {
      console.error('Failed to create WebSocket connection:', err)
      setConnectionStatus('error')
      scheduleReconnect()
    }
  }, [socket, scheduleReconnect]) // eslint-disable-line react-hooks/exhaustive-deps

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false // PREVENT auto-reconnect
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }

    if (socket) {
      socket.close(1000, 'Normal closure')
      setSocket(null)
    }
    setConnectionStatus('disconnected')
    reconnectAttemptsRef.current = 0
  }, [socket])

  // Store connect function in ref for access inside verify/reconnect timers
  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  // --- Effects ---

  // Auth-based connection lifecycle
  useEffect(() => {
    if (accessToken && !socket) {
      connect()
    } else if (!accessToken && socket) {
      disconnect()
    }

    return () => {
       // On unmount only? No, this runs on deps change.
       // We rely on 'disconnect' being called when accessToken becomes null.
    }
  }, [accessToken, socket, connect, disconnect])


  // Visibility Change Handling (Mobile)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAndroid()) {
        if (!socket || socket.readyState !== WebSocket.OPEN) {
           // Force check
           if (sessionRef.current?.access_token) connect()
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [socket, connect])

  // --- API Methods ---

  const sendMessage = useCallback((type, data = null, gameId = null) => {
    if (socket?.readyState !== WebSocket.OPEN) return false
    try {
      socket.send(JSON.stringify({ type, game_id: gameId, data: data || null }))
      return true
    } catch (err) {
      console.error('Failed to send WebSocket message:', err)
      return false
    }
  }, [socket])

  // Auto-join user games
  useEffect(() => {
    if (connectionStatus === 'connected' && socket) {
      sendMessage('join_user_games')
    }
  }, [connectionStatus, socket, sendMessage])

  const subscribe = useCallback((messageType, callback) => {
    if (!eventBusRef.current.has(messageType)) {
        eventBusRef.current.set(messageType, new Set())
    }
    eventBusRef.current.get(messageType).add(callback)
    return () => {
        const listeners = eventBusRef.current.get(messageType)
        if (listeners) {
            listeners.delete(callback)
            if (listeners.size === 0) eventBusRef.current.delete(messageType)
        }
    }
  }, [])

  // Legacy Wrappers
  const addMessageHandler = useCallback((type, handler) => subscribe(type, handler), [subscribe])
  
  const removeMessageHandler = useCallback((messageType, handler) => {
      // Manual removal for legacy code that doesn't use the subscribe return value
      const listeners = eventBusRef.current.get(messageType)
      if (listeners) {
          listeners.delete(handler)
          if (listeners.size === 0) eventBusRef.current.delete(messageType)
      }
  }, []) // No dependencies needed as ref is stable

  // Game Helpers
  const joinGame = useCallback((id) => sendMessage('join_game', null, id), [sendMessage])
  const leaveGame = useCallback(() => sendMessage('leave_game'), [sendMessage])
  const sendMove = useCallback((id, move) => sendMessage('move', move, id), [sendMessage])
  const sendChat = useCallback((id, msg) => sendMessage('chat', { message: msg }, id), [sendMessage])
  const spectateGame = useCallback((id) => {
      sendMessage('spectate_game', null, id)
      return Promise.resolve() // Mock promise for legacy API
  }, [sendMessage])
  const leaveSpectate = useCallback(() => sendMessage('leave_spectate'), [sendMessage])

  const value = {
    socket,
    connectionStatus,
    connect,
    disconnect,
    sendMessage,
    subscribe,
    addMessageHandler,
    removeMessageHandler,
    joinGame,
    leaveGame,
    sendMove,
    sendChat,
    spectateGame,
    leaveSpectate,
    isConnected: connectionStatus === 'connected'
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}
